import React from 'react';
import { Heart, Users, GraduationCap, Stethoscope, HandHeart, Home } from 'lucide-react';
import { socialPrograms } from '../mock';
import { Card, CardContent } from './ui/card';

const iconMap = {
  'education': GraduationCap,
  'healthcare': Stethoscope,
  'empowerment': Users,
  'eldercare': Heart,
  'orphan': HandHeart,
  'community': Home
};

export const SocialWork = () => {
  return (
    <section id="social-work" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
            Social Impact
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Community Upliftment Programs
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-600 to-teal-600 mx-auto rounded-full mb-6"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            Transforming lives through comprehensive social welfare initiatives focused on education, 
            healthcare, empowerment, and community development.
          </p>
        </div>

        {/* Hero Image Section */}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl">
          <div className="grid md:grid-cols-2 gap-0">
            <img
              src="https://res.cloudinary.com/db6zl4j0y/image/upload/v1769461844/close-up-volunteers-working-together_gtlvol.jpg"
              alt="Community Service"
              className="w-full h-80 md:h-full object-cover"
            />
            <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-10 flex flex-col justify-center text-white">
              <h3 className="text-3xl font-bold mb-4">
                Making a Real Difference
              </h3>
              <p className="text-emerald-100 mb-6 leading-relaxed">
                Our comprehensive social programs touch every aspect of community life, from education 
                and healthcare to women's empowerment and elder care. Each initiative is designed to 
                create lasting, sustainable impact.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold">Many Lives</div>
                  <div className="text-sm text-emerald-100">Lives Touched</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold">2+</div>
                  <div className="text-sm text-emerald-100">Active Programs</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {socialPrograms.map((program, index) => {
            const IconComponent = iconMap[program.image] || Heart;
            
            return (
              <Card 
                key={program.id}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Icon Section */}
                  <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-8 relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-200/20 rounded-full"></div>
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-teal-200/20 rounded-full"></div>
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {program.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {program.description}
                    </p>
                    <div className="flex items-center space-x-2 bg-emerald-50 rounded-lg px-4 py-3">
                      <Users className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm font-semibold text-emerald-700">
                        {program.beneficiaries} beneficiaries
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Impact Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-15">
          {[
            { value: '', label: 'Students Educated', icon: GraduationCap },
            { value: '1', label: 'Health Checkups', icon: Stethoscope },
            /*{ value: '3,000+', label: 'Women Empowered', icon: Users },*/
            { value: '', label: 'Orphans Supported', icon: HandHeart }
          ].map((stat, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 text-center transform hover:scale-105 transition-transform duration-300"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
